import { createStore } from 'polotno/model/store';
import { applyTemplateToProduct, getTemplate } from './templateService';

/**
 * Apply template to a product and download the image
 * @param {string} templateId - Template ID
 * @param {Object} product - Product data
 * @param {Function} onProgress - Progress callback (current, total, status)
 * @returns {Promise<{success: boolean, productTitle: string, error?: string}>}
 */
async function applyTemplateAndDownload(templateId, product, onProgress) {
    let store = null;
    let workspaceElement = null;

    try {
       

        // Create a new Polotno store for this product
        store = createStore({
            key: process.env.REACT_APP_POLOTNO_API_KEY,
            showCredit: true,
        });

        // Apply template to product
        const modifiedJson = applyTemplateToProduct(templateId, product);

        // Load the JSON into the store
        store.loadJSON(modifiedJson, true);

        // Wait for store to be ready
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get pages
        const pages = store.pages || [];
        if (pages.length === 0) {
            throw new Error('No pages found in template');
        }

        // Create a hidden container and mount Workspace with proper Polotno structure
        const container = document.createElement('div');
        container.style.position = 'fixed';
        container.style.top = '-10000px';
        container.style.left = '-10000px';
        container.style.width = '1080px';
        container.style.height = '1080px';
        container.style.visibility = 'hidden';
        container.style.zIndex = '-1';
        document.body.appendChild(container);

        // Dynamically import required Polotno components
        const { Workspace } = await import('polotno/canvas/workspace');
        const { PolotnoContainer, WorkspaceWrap } = await import('polotno');
        const React = (await import('react')).default;
        const ReactDOM = (await import('react-dom/client')).default;

        // Create root and render with proper Polotno structure
        const root = ReactDOM.createRoot(container);
        root.render(
            React.createElement(
                PolotnoContainer,
                { style: { width: '1080px', height: '1080px' } },
                React.createElement(
                    WorkspaceWrap,
                    null,
                    React.createElement(Workspace, { store, layout: 'horizontal' })
                )
            )
        );
        workspaceElement = container;

        // Wait for workspace to fully mount and initialize canvas
        // Polotno needs time to create the Konva stage
        await new Promise((resolve) => setTimeout(resolve, 2500));

        // Additional wait to ensure canvas is ready
        // Check if store has active page and stage is ready
        let attempts = 0;
        while (attempts < 10) {
            const activePage = store.activePage;
            if (activePage) {
                // Try to access the stage to verify it's mounted
                try {
                    const stage = activePage.stage;
                    if (stage) {
                        break;
                    }
                } catch (e) {
                    // Stage not ready yet
                }
            }
            await new Promise((resolve) => setTimeout(resolve, 300));
            attempts++;
        }

        // Download each page
        for (let index = 0; index < pages.length; index++) {
            const page = pages[index];
            store.selectPage(page.id);

            // Wait for page to render and verify stage exists
            await new Promise((resolve) => setTimeout(resolve, 1000));

            // Verify the stage is available for this page
            const activePage = store.activePage;
            if (!activePage) {
                throw new Error(`Active page not found for page ${page.id}`);
            }

            // Check if stage exists
            let stageReady = false;
            for (let checkAttempt = 0; checkAttempt < 5; checkAttempt++) {
                try {
                    const stage = activePage.stage;
                    if (stage) {
                        stageReady = true;
                        break;
                    }
                } catch (e) {
                    // Stage not ready, wait a bit more
                }
                await new Promise((resolve) => setTimeout(resolve, 400));
            }

            if (!stageReady) {
                throw new Error(`Canvas stage not mounted for page ${page.id || page.name || index + 1}. Workspace may not be fully initialized.`);
            }

            const productName = (product.title || product.id || 'product')
                .replace(/[^a-z0-9]/gi, '_')
                .substring(0, 50);

            const fileName = pages.length > 1
                ? `${productName}_page_${index + 1}`
                : productName;

            // Now saveAsImage should work since Workspace is mounted and stage is ready
            try {
                store.saveAsImage({
                    fileName: fileName,
                });
            } catch (saveError) {
                throw new Error(`Failed to save image for ${fileName}: ${saveError.message}`);
            }

            if (index < pages.length - 1) {
                await new Promise((resolve) => setTimeout(resolve, 600));
            }
        }

        // Cleanup
        root.unmount();
        if (workspaceElement && workspaceElement.parentNode) {
            workspaceElement.parentNode.removeChild(workspaceElement);
        }
        if (store && store.destroy) {
            store.destroy();
        }

        return {
            success: true,
            productTitle: product.title || product.id || 'Unknown Product',
        };
    } catch (error) {
        console.error('Error applying template and downloading:', error);

        // Cleanup on error
        if (workspaceElement && workspaceElement.parentNode) {
            const root = workspaceElement._reactRootContainer;
            if (root) {
                try {
                    root.unmount();
                } catch (e) { }
            }
            workspaceElement.parentNode.removeChild(workspaceElement);
        }
        if (store && store.destroy) {
            store.destroy();
        }

        return {
            success: false,
            productTitle: product.title || product.id || 'Unknown Product',
            error: error.message || 'Unknown error',
        };
    }
}

/**
 * Batch apply template to multiple products and download all
 * @param {string} templateId - Template ID
 * @param {Array} products - Array of product objects
 * @param {Function} onProgress - Progress callback (current, total, status)
 * @returns {Promise<{success: Array, errors: Array}>}
 */
export async function batchApplyTemplateAndDownload(templateId, products, onProgress) {
    const template = getTemplate(templateId);
    if (!template) {
        throw new Error('Template not found');
    }

    const results = {
        success: [],
        errors: [],
    };

    const total = products.length;

    for (let i = 0; i < products.length; i++) {
        const product = products[i];

        onProgress?.(i + 1, total, `Processing ${product.title || product.id}...`);

        const result = await applyTemplateAndDownload(templateId, product, onProgress);

        if (result.success) {
            results.success.push(result);
        } else {
            results.errors.push(result);
        }

        // Small delay between products to avoid overwhelming the browser
        if (i < products.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 200));
        }
    }

    onProgress?.(total, total, 'Completed');

    return results;
}

const batchTemplateService = {
    batchApplyTemplateAndDownload,
    applyTemplateAndDownload,
};

export default batchTemplateService;
